import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react/headless';

export interface SelectOption {
  label: React.ReactNode;
  value: string | number;
  disabled?: boolean;
  [key: string]: any;
}

export interface SelectProps {
  /** 选项列表 */
  options: SelectOption[];
  /** 当前选中值 */
  value?: string | number;
  /** 默认选中值 */
  defaultValue?: string | number;
  /** 选中改变回调 */
  onChange?: (value: string | number, option?: SelectOption) => void;
  /** 占位符 */
  placeholder?: string;
  /** 是否处于加载中 */
  loading?: boolean;
  /** 加载中提示文案 */
  loadingText?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否支持可清空选项 */
  clearable?: boolean;
  /** 尺寸风格 */
  size?: 'sm' | 'md' | 'lg';
  /** 最外层容器额外类名 */
  className?: string;
  /** 触发器按钮额外类名 */
  selectClassName?: string;
  /** 下拉面板额外类名 */
  dropdownClassName?: string;
  /** 错误信息 */
  error?: string;
  /** 表单 name 属性 */
  name?: string;
  /** 是否严格等宽 (如果为 true，强行限制下拉面板宽度等于触发器宽度；默认为 false，保证内容不截断) */
  matchWidth?: boolean;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?: 'auto' | 'auto-start' | 'auto-end' | 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'right' | 'right-start' | 'right-end' | 'left' | 'left-start' | 'left-end';
}

// Popper Modifier 让下拉菜单最小宽度与触发元素对齐，允许根据最长选项自然展开
const minWidthModifier = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite' as const,
  requires: ['computeStyles'],
  fn: ({ state }: any) => {
    state.styles.popper.minWidth = `${state.rects.reference.width}px`;
    state.styles.popper.width = 'max-content';
    state.styles.popper.maxWidth = '90vw';
  },
  effect: ({ state }: any) => {
    const refWidth = state.elements.reference.getBoundingClientRect().width;
    state.elements.popper.style.minWidth = `${refWidth}px`;
    state.elements.popper.style.width = 'max-content';
    state.elements.popper.style.maxWidth = '90vw';
  },
};

// Popper Modifier 严格限制宽度等于触发元素
const exactWidthModifier = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite' as const,
  requires: ['computeStyles'],
  fn: ({ state }: any) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }: any) => {
    const refWidth = state.elements.reference.getBoundingClientRect().width;
    state.elements.popper.style.width = `${refWidth}px`;
  },
};

export const Select: React.FC<SelectProps> = ({
  options = [],
  value,
  defaultValue,
  onChange,
  placeholder = '请选择',
  loading = false,
  loadingText = '加载中...',
  disabled = false,
  clearable = false,
  size = 'md',
  className = '',
  selectClassName = '',
  dropdownClassName = '',
  error,
  name,
  matchWidth = false,
  placement,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string | number | undefined>(
    value !== undefined ? value : defaultValue
  );

  // 同步外部 value 变化
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const selectedOption = options.find((opt) => String(opt.value) === String(internalValue));

  const handleSelect = (option: SelectOption) => {
    if (option.disabled || disabled) return;
    if (value === undefined) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (value === undefined) {
      setInternalValue('');
    }
    onChange?.('');
  };

  const toggleOpen = () => {
    if (!disabled && !loading) {
      setIsOpen((prev) => !prev);
    }
  };

  // 尺寸映射表
  const sizeStyles = {
    sm: 'h-9 px-3 text-xs rounded-md',
    md: 'h-[46px] px-4 text-sm rounded-lg',
    lg: 'h-12 px-5 text-base rounded-xl',
  };

  return (
    <div className={`relative w-full ${className}`}>
      {/* 隐藏的 input 便于原生表单收集 */}
      {name && <input type="hidden" name={name} value={internalValue !== undefined ? String(internalValue) : ''} />}

      <Tippy
        interactive={true}
        visible={isOpen}
        onClickOutside={() => setIsOpen(false)}
        placement={placement}
        offset={[0, 4]}
        popperOptions={{
          modifiers: [matchWidth ? exactWidthModifier : minWidthModifier],
        }}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            className={`z-[100] max-h-60 overflow-y-auto rounded-xl border border-background-200 bg-white p-1.5 shadow-lg shadow-black/5 animate-in fade-in slide-in-from-top-2 duration-150 outline-none ${dropdownClassName}`}
          >
            {options.length === 0 ? (
              <div className="px-4 py-3 text-center text-xs text-foreground-400">暂无数据</div>
            ) : (
              options.map((option) => {
                const isSelected = String(option.value) === String(internalValue);
                return (
                  <div
                    key={String(option.value)}
                    onClick={() => handleSelect(option)}
                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed bg-transparent text-foreground-300'
                        : isSelected
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'hover:bg-background-100/70 text-foreground-800'
                    }`}
                  >
                    <span className="whitespace-nowrap pr-2">{option.label}</span>
                    {isSelected && <i className="ri-check-line text-base text-primary-600 shrink-0 ml-2"></i>}
                  </div>
                );
              })
            )}
          </div>
        )}
      >
        {/* 选择框触发器 */}
        <button
          type="button"
          onClick={toggleOpen}
          disabled={disabled || loading}
          className={`w-full flex items-center justify-between bg-white border border-background-300/60 text-left outline-none transition-all duration-200 cursor-pointer ${
            sizeStyles[size]
          } ${
            isOpen
              ? 'border-primary-400/80 ring-2 ring-primary-400/20 shadow-sm'
              : 'hover:border-background-400/80'
          } ${error ? 'border-red-400 focus:border-red-400 ring-2 ring-red-400/20' : ''} ${
            disabled || loading ? 'bg-gray-50 text-foreground-400 cursor-not-allowed border-gray-200' : 'text-foreground-950'
          } ${selectClassName}`}
        >
          <span className="truncate flex-1 pr-2">
            {loading ? (
              <span className="text-foreground-400 flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin text-base"></i>
                {loadingText}
              </span>
            ) : selectedOption ? (
              <span className="text-foreground-950">{selectedOption.label}</span>
            ) : (
              <span className="text-foreground-400">{placeholder}</span>
            )}
          </span>

          <span className="flex items-center gap-1.5 shrink-0 text-foreground-400">
            {clearable && selectedOption && !disabled && !loading && (
              <span
                onClick={handleClear}
                className="p-0.5 hover:text-foreground-700 hover:bg-foreground-100 rounded-full transition-colors"
                title="清空"
              >
                <i className="ri-close-line text-sm"></i>
              </span>
            )}
            <i
              className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${
                isOpen ? 'rotate-180 text-primary-500' : ''
              }`}
            ></i>
          </span>
        </button>
      </Tippy>

      {/* 错误提示 */}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default Select;
